'use client';

import { Dialog as BaseDialog } from '@base-ui-components/react/dialog';
import { type VariantProps, cva } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import type { CSSProperties, ComponentProps } from 'react';
import { baseUiOverlayStyles, cn } from '../../lib/utils';

const Sheet = BaseDialog.Root;

const SheetTrigger = BaseDialog.Trigger;

const SheetClose = BaseDialog.Close;

const SheetPortal = BaseDialog.Portal;

function SheetBackdrop({
  className,
  ...props
}: ComponentProps<typeof BaseDialog.Backdrop>) {
  return (
    <BaseDialog.Backdrop
      className={cn(baseUiOverlayStyles, className)}
      {...props}
    />
  );
}

const sheetVariants = cva(
  'fixed z-[60] flex w-full flex-col bg-brand-white p-0 shadow-lg transition ease-in-out data-[closed]:animate-out data-[open]:animate-in data-[closed]:duration-200 data-[open]:duration-300 lg:data-[closed]:duration-300 lg:data-[open]:duration-500',
  {
    variants: {
      side: {
        top: 'data-[closed]:slide-out-to-top data-[open]:slide-in-from-top inset-x-0 top-0',
        bottom:
          'data-[closed]:slide-out-to-bottom data-[open]:slide-in-from-bottom inset-x-0 bottom-0',
        left: 'data-[closed]:slide-out-to-left data-[open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 sm:max-w-md',
        right:
          'data-[closed]:slide-out-to-right data-[open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 sm:max-w-md',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  }
);

interface SheetNewContentProps
  extends ComponentProps<typeof BaseDialog.Popup>,
    VariantProps<typeof sheetVariants> {
  overlayClassName?: string;
  overlayStyle?: CSSProperties;
  seoMode?: boolean;
}

function SheetContent({
  side = 'right',
  className,
  overlayClassName,
  overlayStyle,
  children,
  seoMode,
  ...props
}: SheetNewContentProps) {
  if (seoMode) {
    return (
      <>
        <SheetPortal>
          <SheetBackdrop className={overlayClassName} style={overlayStyle} />
          <BaseDialog.Popup
            className={cn(sheetVariants({ side }), className)}
            {...props}
          >
            {children}
          </BaseDialog.Popup>
        </SheetPortal>
        <div className="sr-only" aria-hidden="true">
          {children}
        </div>
      </>
    );
  }
  return (
    <SheetPortal>
      <SheetBackdrop className={overlayClassName} style={overlayStyle} />
      <BaseDialog.Popup
        className={cn(sheetVariants({ side }), className)}
        {...props}
      >
        {children}
      </BaseDialog.Popup>
    </SheetPortal>
  );
}

function SheetBody({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div className={cn('overflow-y-auto px-6', className)} {...props}>
      {children}
    </div>
  );
}

function SheetHeader({ className, children, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'relative flex flex-col gap-y-2 border-b px-6 py-4 text-center sm:text-left',
        className
      )}
      {...props}
    >
      <BaseDialog.Close className="absolute top-4 right-6 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[open]:bg-secondary">
        <XIcon className="size-5 text-neutral-700" />
        <span className="sr-only">Close</span>
      </BaseDialog.Close>
      {children}
    </div>
  );
}

function SheetFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
        className
      )}
      {...props}
    />
  );
}

function SheetTitle({
  className,
  render,
  ...props
}: ComponentProps<typeof BaseDialog.Title>) {
  return (
    <BaseDialog.Title
      render={render}
      className={cn('font-semibold text-foreground text-lg', className)}
      {...props}
    />
  );
}

function SheetDescription({
  className,
  ...props
}: ComponentProps<typeof BaseDialog.Description>) {
  return (
    <BaseDialog.Description
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Sheet,
  SheetBackdrop,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
